import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { errorView } from '@krp/client-runtime'
import {
  PhotoRegistryPanel,
  buildPhotoRegistryVm,
  newUuid,
  platformPhotoDecision,
  platformPhotoDeleteFromSource,
  platformPhotoDeleteStaging,
  platformPhotoRegistry,
  platformPhotoSend,
  platformPhotoSendJob,
  platformPhotoSources,
  platformPhotoStaging,
  deleteProgressOfJob,
  deleteRefusedProgress,
  deleteUnchangedProgress,
  deletingProgress,
  progressOfJob,
  refusedProgress,
  removalKey,
  sendingProgress,
  unchangedProgress,
  type PairVm,
  type PhotoRegistryActions,
  type PhotoTileVm,
  type SendProgressVm,
  type SlotVm,
} from '@krp/platform-ui'

import { useStagingPhotoAdd } from './useStagingPhotoAdd'

/** Как часто читаем ход отправки у службы синхронизации, пока задача не закончилась. */
const JOB_POLL_MS = 1500

interface JobRef {
  jobId: string
  title: string
  /** Что делает задача: отправляет фото в источник или удаляет его оттуда. */
  kind: 'send' | 'delete'
  /** Ключ хода в состоянии окна: `slotKey` у отправки, `removalKey` у удаления. */
  key: string
}

const jobId = (kind: JobRef['kind'], key: string): string => `${kind}|${key}`

const without = <T,>(record: Readonly<Record<string, T>>, key: string): Record<string, T> => Object.fromEntries(Object.entries(record).filter(([name]) => name !== key))

/**
 * Читает ход одной задачи (отправки или удаления) и отдаёт его окну словами. Пока задача идёт — опрашивает; закончилась (успех или отказ источника) —
 * перестаёт. Сбой самого чтения хода не выдаётся за отказ отправки: исход неизвестен, текст сервера показан дословно.
 */
function SendJobProbe({ job, onView }: { job: JobRef; onView: (job: JobRef, view: SendProgressVm) => void }) {
  const [settled, setSettled] = useState(false)
  const result = platformPhotoSendJob.useQuery({ jobId: job.jobId }, { pollingInterval: settled ? 0 : JOB_POLL_MS })
  const { data, error } = result
  useEffect(() => {
    if (data !== undefined) {
      const view = job.kind === 'delete' ? deleteProgressOfJob(data, job.title) : progressOfJob(data, job.title)
      setSettled(view.phase === 'done' || view.phase === 'failed')
      onView(job, view)
    } else if (error !== undefined) {
      const failure = errorView(error)
      onView(job, {
        phase: 'running',
        text:
          job.kind === 'delete'
            ? `Ход удаления из ${job.title} не прочитан: удаление могло пройти`
            : `Ход отправки в ${job.title} не прочитан: отправка могла пройти`,
        error: { code: failure.code, message: failure.message, requestId: failure.correlationId },
      })
    }
  }, [data, error, job, onView])
  return null
}

/**
 * Binding окна фото общего товара (PHI-8, RP-PSR-3, RP-PSR-7): реестр, источники из ответа сервера, решения «Это одно / Разные», «Добавить фото» (вырез, рамка, предпросмотр, «Сохранить») отправка в источник и удаление (временной загрузки и из источника). Реестр — данные сервера
 * (RTK Query); файл загрузки идёт мимо кэша (`File` в Redux класть нельзя). Живые временные загрузки (сервер хранит их семь суток) читаются списком,
 * поэтому после перезагрузки окна плитки «Пока нигде не лежит» возвращаются. Ключ операции отправки — на одно намерение человека: повтор после обрыва ответа несёт тот же ключ.
 */
export function CommonPhotoRegistryBinding({ productId }: { productId: string }) {
  const args = useMemo(() => ({ productId }), [productId])
  const result = platformPhotoRegistry.useQuery(args, { skip: productId === '' })
  const stagingResult = platformPhotoStaging.useQuery(args, { skip: productId === '' })
  // Источники окна — только из ответа сервера (подписи, что умеет вид, пределы): список из связей товара на клиенте не собирается.
  const sourcesResult = platformPhotoSources.useQuery(args, { skip: productId === '' })
  const [decide, decideResult] = platformPhotoDecision.useMutation()
  const [sendPhoto] = platformPhotoSend.useMutation()
  const [deleteStaging] = platformPhotoDeleteStaging.useMutation()
  const [deleteFromSource] = platformPhotoDeleteFromSource.useMutation()
  const [sends, setSends] = useState<Readonly<Record<string, SendProgressVm>>>({})
  const [removals, setRemovals] = useState<Readonly<Record<string, SendProgressVm>>>({})
  const [jobs, setJobs] = useState<Readonly<Record<string, JobRef>>>({})
  const operations = useRef(new Map<string, string>())

  const refetch = result.refetch
  const refetchStaging = stagingResult.refetch
  const refetchSources = sourcesResult.refetch
  const add = useStagingPhotoAdd(productId, () => {
    void refetchStaging()
  })
  const operationOf = useCallback((key: string): string => {
    const known = operations.current.get(key)
    if (known !== undefined) return known
    const fresh = newUuid()
    operations.current.set(key, fresh)
    return fresh
  }, [])

  const onJobView = useCallback(
    (job: JobRef, view: SendProgressVm) => {
      const slotKey = job.key
      if (job.kind === 'delete') {
        setRemovals((prev) => ({ ...prev, [slotKey]: view }))
        if (view.phase === 'done') {
          // Удаление прошло: ключ намерения больше не нужен. Отказ ключ сохраняет — «Повторить» несёт тот же.
          operations.current.delete(`delete|${slotKey}`)
          void refetch()
          void refetchStaging()
        }
        return
      }
      setSends((prev) => ({ ...prev, [slotKey]: view }))
      if (view.phase === 'done' || view.phase === 'failed') {
        // Задача закончилась: следующая отправка того же — новое намерение, а прежний ключ уже ничего не значит.
        for (const key of [...operations.current.keys()]) if (key.startsWith(`${slotKey}|`)) operations.current.delete(key)
        if (view.phase === 'done') {
          void refetch()
          void refetchStaging()
        }
      }
    },
    [refetch, refetchStaging],
  )

  const startDelete = useCallback(
    (tile: PhotoTileVm, slot: SlotVm) => {
      const key = removalKey(tile, slot.resourceId)
      const intent = `delete|${key}`
      setRemovals((prev) => ({ ...prev, [key]: deletingProgress(slot.title) }))
      // Повтор после отказа: прежний читатель задачи снимается, новый прочитает ход заново.
      setJobs((prev) => without(prev, jobId('delete', key)))
      void deleteFromSource({ productId, photoKey: tile.photoKey ?? '', resourceId: slot.resourceId, operationId: operationOf(intent) })
        .unwrap()
        .then((sent) => {
          if (sent.jobId === null) {
            operations.current.delete(intent)
            setRemovals((prev) => ({ ...prev, [key]: deleteUnchangedProgress() }))
            void refetch()
            return
          }
          const id = sent.jobId
          setJobs((prev) => ({ ...prev, [jobId('delete', key)]: { jobId: id, title: slot.title, kind: 'delete', key } }))
        })
        .catch((error: unknown) => {
          const failure = errorView(error)
          // Ключ сохраняется при любом отказе: «Повторить» — то же намерение, второго удаления не будет.
          setRemovals((prev) => ({ ...prev, [key]: deleteRefusedProgress(failure, failure.kind === 'unknown-outcome') }))
        })
    },
    [deleteFromSource, operationOf, productId, refetch],
  )

  const startRemoveStaged = useCallback(
    (tile: PhotoTileVm) => {
      const key = removalKey(tile, null)
      setRemovals((prev) => ({ ...prev, [key]: deletingProgress(null) }))
      void deleteStaging({ productId, stagingId: tile.stagingId ?? '' })
        .unwrap()
        .then(() => {
          setRemovals((prev) => without(prev, key))
          void refetchStaging()
          void refetch()
        })
        .catch((error: unknown) => {
          const failure = errorView(error)
          setRemovals((prev) => ({ ...prev, [key]: deleteRefusedProgress(failure, failure.kind === 'unknown-outcome') }))
        })
    },
    [deleteStaging, productId, refetch, refetchStaging],
  )

  const actions = useMemo<PhotoRegistryActions>(
    () => ({
      reload: () => {
        void refetch()
        void refetchStaging()
        void refetchSources()
      },
      addFile: add.start,
      previewBox: add.preview,
      editBox: add.edit,
      saveAdded: add.save,
      cancelAdd: add.cancel,
      send: (tile: PhotoTileVm, slot: SlotVm, replace: boolean) => {
        const intent = `${slot.key}|${String(replace)}`
        setSends((prev) => ({ ...prev, [slot.key]: sendingProgress(slot.title) }))
        void sendPhoto({
          productId,
          resourceId: slot.resourceId,
          operationId: operationOf(intent),
          replaceExisting: replace,
          ...(tile.stagingId !== null ? { stagingId: tile.stagingId } : { photoKey: tile.photoKey ?? '' }),
        })
          .unwrap()
          .then((sent) => {
            if (sent.jobId === null) {
              operations.current.delete(intent)
              setSends((prev) => ({ ...prev, [slot.key]: unchangedProgress() }))
              void refetch()
              void refetchStaging()
              return
            }
            const id = sent.jobId
            setJobs((prev) => ({ ...prev, [jobId('send', slot.key)]: { jobId: id, title: slot.title, kind: 'send', key: slot.key } }))
          })
          .catch((error: unknown) => {
            const failure = errorView(error)
            // «Исход неизвестен» — ключ сохраняется: повтор не создаст вторую задачу; отказ сервера — новое намерение.
            if (failure.kind !== 'unknown-outcome') operations.current.delete(intent)
            setSends((prev) => ({ ...prev, [slot.key]: refusedProgress(failure) }))
          })
      },
      dismissSend: (slot: SlotVm) => {
        setSends((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== slot.key)))
        setJobs((prev) => without(prev, jobId('send', slot.key)))
      },
      removeStaged: startRemoveStaged,
      deleteFromSource: startDelete,
      dismissDelete: (tile: PhotoTileVm, slot: SlotVm | null) => {
        const key = removalKey(tile, slot?.resourceId ?? null)
        operations.current.delete(`delete|${key}`)
        setRemovals((prev) => without(prev, key))
        setJobs((prev) => without(prev, jobId('delete', key)))
      },
      decide: (pair: PairVm, decision: 'same' | 'different') => {
        void decide({ productId, keyA: pair.keyA, keyB: pair.keyB, decision })
      },
    }),
    [add.cancel, add.edit, add.preview, add.save, add.start, decide, operationOf, productId, refetch, refetchSources, refetchStaging, sendPhoto, startDelete, startRemoveStaged],
  )

  const vm = buildPhotoRegistryVm({
    result: {
      ...(result.data !== undefined ? { data: result.data } : {}),
      ...(result.error !== undefined ? { error: result.error } : {}),
      isUninitialized: result.isUninitialized,
      isLoading: result.isFetching && result.data === undefined,
      isError: result.isError,
      isSuccess: result.isSuccess,
    },
    sources: sourcesResult.data ?? [],
    staged: stagingResult.data ?? [],
    sends,
    removals,
    decision: decideResult,
    adding: add.adding,
    sourcesError: sourcesResult.isError && sourcesResult.error !== undefined ? errorOf(sourcesResult.error) : null,
  })

  return (
    <>
      <PhotoRegistryPanel vm={vm} actions={actions} />
      {Object.entries(jobs).map(([id, job]) => (
        <SendJobProbe key={`${id}|${job.jobId}`} job={job} onView={onJobView} />
      ))}
    </>
  )
}

function errorOf(error: unknown) {
  const view = errorView(error)
  return { kind: view.kind, code: view.code, message: view.message, correlationId: view.correlationId }
}
